import { inject, Injectable } from '@angular/core';
import { PermissionsService } from '@shared/services/permissions.service';
import {
  IAnswer,
  IModule,
  IQuestion,
  IQuestionStatus,
  Questionnaire,
  SMEQC,
} from '../models';
import {
  BehaviorSubject,
  mergeMap,
  Observable,
  of,
  Subject,
  take,
  tap,
} from 'rxjs';
import { QuestionnaireService } from './questionnaire.service';

import {
  BasePermission,
  PermissionGrants,
} from '@shared/interfaces/permissions.interface';
import { UtilsService } from '@shared/services/utils.service';
import { MessagesService } from './messages.service';
import { SubmissionsService } from './submissions.service';
import { DocumentsService } from '@documents/services/documents.service';

@Injectable()
export class QuestionnaireDetailFacadeService {
  private readonly _questionnaireService = inject(QuestionnaireService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _messageService = inject(MessagesService);
  private readonly _utilsService = inject(UtilsService);
  private readonly _submissionService = inject(SubmissionsService);
  private readonly _documentService = inject(DocumentsService);
  sectionDetail$: Observable<any> = this._questionnaireService.section$.pipe(
    mergeMap((section) => {
      return of(section);
    }),
  );

  // TODO: Remove previewMode & previewMode$
  previewMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  previewMode$ = this.previewMode
    .asObservable()
    .pipe(tap((val) => console.log('previewMode:', val)));

  // TODO: Remove focusInitialSection & focusInitialSection$
  private focusInitialSectionSubject = new Subject<string>();
  public focusField = new BehaviorSubject<boolean>(false);
  focusInitialSection$ = this.focusInitialSectionSubject.asObservable();

  getSavingModuleMark(moduleId: string, mark: SMEQC) {
    return this._questionnaireService.getSavingModuleMark(moduleId, mark);
  }

  getSavingAnswerMark(answerId: string, mark: SMEQC) {
    return this._questionnaireService.getSavingAnswerMark(answerId, mark);
  }

  getSavingModuleAnswerMarks(moduleId: string, mark: SMEQC) {
    return this._questionnaireService.getSavingModuleAnswerMarks(
      moduleId,
      mark,
    );
  }

  documentType(submissionTypeId: string, entity_type: string) {
    return this._documentService.getDocumentTypes(
      submissionTypeId,
      entity_type,
    );
  }

  documents(submissionId: string) {
    return this._documentService.getDocuments(submissionId, 'workspace');
  }

  saveAnswer(answer: IAnswer | IAnswer[]) {
    this._questionnaireService.handleQuestionAnswer(answer);
  }

  setSection(index: number) {
    this._questionnaireService.setActiveSection(index);
  }

  getSectionByName(title: string) {
    this._questionnaireService.getSectionByName(title);
  }

  clearActiveSection() {
    this._questionnaireService.clearActiveSection();
  }

  getQuestionVisibility(questionId: string) {
    return this._questionnaireService.getQuestionVisibility(questionId);
  }

  getModuleVisibility(moduleId: string) {
    return this._questionnaireService.getModuleVisibility(moduleId);
  }

  getModuleAnswerCounts(moduleId: string) {
    return this._questionnaireService.getModuleAnswerCounts(moduleId);
  }

  getQuestionnaire(submissionId: string, questionnaireId: string) {
    return this._questionnaireService.getQuestionnaire(
      submissionId,
      questionnaireId,
    );
  }

  getQuestionnaireState(submissionId: string) {
    return this._questionnaireService.getQuestionnaireState(submissionId);
  }

  getQuestion(submissionId: string, questionId: string) {
    return this._questionnaireService.getQuestion(submissionId, questionId);
  }

  getSubmissionQuestionnaire(submissionId: string) {
    return this._questionnaireService.getSubmissionQuestionnaire(submissionId);
  }

  getPreviousModule(moduleId: string) {
    return this._questionnaireService.getPreviousModule(moduleId);
  }

  getNextModule(moduleId: string) {
    return this._questionnaireService.getNextModule(moduleId);
  }

  hasNextModule(moduleId: string) {
    return this._questionnaireService.hasNextModule(moduleId);
  }

  hasPreviousModule(moduleId: string) {
    return this._questionnaireService.hasPreviousModule(moduleId);
  }

  getQuestionnaireChoicesResource(endpoint: string) {
    return this._questionnaireService.getQuestionRouteChoicesChoice(endpoint);
  }

  getQuestionnairePermissions(
    submissionId: string,
    permissions: BasePermission[],
    grants: PermissionGrants[],
  ) {
    return this._questionnaireService
      .getSubmissionQuestionnaire(submissionId)
      .pipe(
        mergeMap((questionnaire: Questionnaire) => {
          return this._permissionsService.observeEntityPermissions(
            questionnaire.id,
            permissions,
            grants,
          );
        }),
      );
  }

  updateModuleMark(
    module: IModule,
    markType: SMEQC,
    qcMark?: boolean,
    smeMark?: boolean,
  ) {
    const payload: any = {};

    if (qcMark !== undefined) {
      payload.qc_mark = qcMark;
    }

    if (smeMark !== undefined) {
      payload.sme_mark = smeMark;
    }

    this._submissionService.submission$
      .pipe(take(1))
      .subscribe((submission) =>
        this._questionnaireService.updateSectionReviewStatus(
          module,
          submission.id,
          payload,
          markType,
        ),
      );
  }

  updateQuestionReviewStatus(questionStatus: IQuestionStatus, markType: SMEQC) {
    const markStatus: any =
      this._utilsService.convertKeysToSnakeCase(questionStatus);
    this._questionnaireService.updateQuestionReviewStatus(markStatus, markType);
  }

  selectThreadFromQuestion(
    questionId: string,
    workspaceId: string,
    entityId: 'answer' | 'document',
  ) {
    this._messageService.selectThread(questionId, workspaceId, entityId);
  }

  selectQuestionFromThread(threadId: string) {
    this._questionnaireService.selectSectionFromThread(threadId);
  }

  addQuestion(questionId: string, subModuleId: string) {
    this._questionnaireService.addQuestion(questionId, subModuleId);
  }

  removeDuplicateQuestion(
    submissionId: string,
    question: IQuestion,
    orderNum: number,
  ) {
    this._questionnaireService.removeDuplicateQuestion(
      submissionId,
      question,
      orderNum,
    );
  }

  focusInitialSection(sectionId: string) {
    this.focusInitialSectionSubject.next(sectionId);
    this.setFocusOnSectionField(true);
  }

  setFocusOnSectionField(value: boolean) {
    this.focusField.next(value);
  }

  checkStateLabel(countryId: string, countryChoices: any[]): string {
    const selectedCountryId = countryId;
    const usaId = countryChoices.find(
      (country: any) => country.label === 'United States',
    )?.id;
    const canadaId = countryChoices.find(
      (country: any) => country.label === 'Canada',
    )?.id;

    switch (selectedCountryId) {
      case usaId:
        return 'State';
      case canadaId:
        return 'Province';
      default:
        return 'State/Province';
    }
  }

  reloadQuestionnaire() {
    this._questionnaireService.reloadQuestionnaire(true);
  }

  resubmitQuestionnaire(submissionId: string) {
    this._questionnaireService
      .resubmitQuestionnaire(submissionId)
      .subscribe(() => {
        this._messageService.getMessages(submissionId).subscribe();
      });
  }
}
